/* eslint-disable react/require-default-props -- React deprecó defaultProps en componentes
   de función (avisa por consola en cada render): los valores por defecto van en la firma. */
import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useMutation, useQuery } from '@apollo/client';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  IconButton,
  InputAdornment,
  Skeleton,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import {
  ArrowDownward,
  ArrowUpward,
  Close,
  DeleteOutline,
  QueueMusic,
  Search
} from '@mui/icons-material';
import { premiumButtonSx } from 'common/theme/button/premiumButtonSx';
import { createSnack } from 'common/utils/general';
import uiSlice from 'store/modules/ui';
import {
  ADMIN_PLAY_MUSIC_ALBUMS,
  PLAY_MUSIC_ALBUMS,
  PLAY_MUSIC_TRACKS,
  SAVE_PLAY_MUSIC_ALBUM
} from 'api/graphql/playMusic';
import { formatDuration } from 'components/play/workoutEstimate';

const EASE = 'cubic-bezier(0.32,0.72,0,1)';

const sectionLabelSx = {
  color: 'text.secondary',
  fontWeight: 700,
  letterSpacing: '0.08em'
};

const listShellSx = {
  borderRadius: '12px',
  border: '1px solid',
  borderColor: 'hairline.main',
  backgroundColor: 'surface.overlay',
  overflow: 'hidden'
};

// Comparación tolerante a mayúsculas y tildes: buscar "relajacion" tiene que encontrar
// "Relajación" (los títulos los escribe la entrenadora, con acentos y todo).
const normalize = (value) =>
  (value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

/**
 * OTRIX Play — armador de playlists, el mismo para el cliente y para el panel admin.
 *
 * Dos zonas: arriba la COLA que se está armando (orden real de reproducción, se mueve con
 * botones como en el editor de pasos: nada de drag and drop) y abajo la librería con
 * buscador y casillas. `personal` decide de quién queda la playlist; el backend además
 * fuerza personal cuando quien guarda es un cliente, así que la UI no es la única defensa.
 */
function PlayAlbumBuilder({ open, album = null, personal = false, onClose, onSaved }) {
  const { t } = useTranslation(['play']);
  const dispatch = useDispatch();

  const [title, setTitle] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [search, setSearch] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [failed, setFailed] = useState(false);

  const { data, loading } = useQuery(PLAY_MUSIC_TRACKS, {
    variables: {},
    skip: !open,
    fetchPolicy: 'cache-and-network'
  });

  const [saveAlbum, { loading: saving }] = useMutation(SAVE_PLAY_MUSIC_ALBUM, {
    // Una playlist personal no toca el catálogo curado del panel admin (y al revés, un
    // cliente no tiene permiso para correr la query de admin).
    refetchQueries: personal
      ? [{ query: PLAY_MUSIC_ALBUMS }]
      : [{ query: ADMIN_PLAY_MUSIC_ALBUMS }, { query: PLAY_MUSIC_ALBUMS }]
  });

  useEffect(() => {
    if (!open) return;
    setTitle(album?.title || '');
    setSelectedIds((album?.tracks || []).map((tr) => String(tr.id)));
    setSearch('');
    setSubmitted(false);
    setFailed(false);
  }, [open, album]);

  const library = useMemo(() => data?.playMusicTracks || [], [data]);

  // Índice por id con la librería MÁS las pistas que ya trae el álbum: una pista que se
  // ocultó después sigue apareciendo en la cola en vez de desaparecer sin avisar.
  const trackById = useMemo(() => {
    const map = new Map();
    (album?.tracks || []).forEach((tr) => map.set(String(tr.id), tr));
    library.forEach((tr) => map.set(String(tr.id), tr));
    return map;
  }, [library, album]);

  const selectedTracks = selectedIds.map((id) => trackById.get(id)).filter(Boolean);
  const totalSeconds = selectedTracks.reduce((acc, tr) => acc + (tr.duration_seconds || 0), 0);

  const filtered = useMemo(() => {
    const term = normalize(search).trim();
    if (!term) return library;
    return library.filter((tr) => normalize(`${tr.title} ${tr.category}`).includes(term));
  }, [library, search]);

  const toggleTrack = (id) =>
    setSelectedIds((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]));

  const move = (index, delta) => {
    const target = index + delta;
    if (target < 0 || target >= selectedIds.length) return;
    const next = [...selectedIds];
    [next[index], next[target]] = [next[target], next[index]];
    setSelectedIds(next);
  };

  const handleSave = async () => {
    setSubmitted(true);
    setFailed(false);
    if (!title.trim() || selectedIds.length === 0) return;

    try {
      const { data: saved } = await saveAlbum({
        variables: {
          input: {
            id: album?.id || null,
            title: title.trim(),
            is_active: album ? Boolean(album.is_active) : true,
            position: album?.position || 0,
            personal,
            track_ids: selectedIds
          }
        }
      });
      dispatch(
        uiSlice.actions.enqueueSnackbar(
          createSnack({
            key: `album-${Date.now()}`,
            variant: 'success',
            message: t('albums.saved', { ns: 'play', defaultValue: 'Playlist guardada.' })
          })
        )
      );
      onSaved?.(saved?.savePlayMusicAlbum || null);
      onClose();
    } catch {
      setFailed(true);
    }
  };

  const titleError = submitted && !title.trim();
  const emptyQueue = submitted && selectedIds.length === 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: 3,
          backgroundColor: 'surface.content',
          backgroundImage: 'none',
          border: '1px solid',
          borderColor: 'hairline.main'
        }
      }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 3, py: 2, borderBottom: '1px solid', borderColor: 'hairline.main' }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
          <QueueMusic sx={{ color: 'gold.main' }} />
          <Typography variant="h6" fontWeight={800} noWrap>
            {album
              ? t('albums.editTitle', { ns: 'play', defaultValue: 'Editar playlist' })
              : t('albums.newTitle', { ns: 'play', defaultValue: 'Nueva playlist' })}
          </Typography>
        </Stack>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ color: 'text.secondary' }}
          aria-label={t('recovery.close', { ns: 'play', defaultValue: 'Cerrar' })}>
          <Close fontSize="small" />
        </IconButton>
      </Stack>

      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          <TextField
            label={t('albums.name', { ns: 'play', defaultValue: 'Nombre de la playlist' })}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
            error={titleError}
            helperText={titleError ? t('albums.nameRequired', { ns: 'play', defaultValue: 'Ponle un nombre.' }) : ''}
            inputProps={{ maxLength: 120 }}
            fullWidth
          />

          {/* Cola en construcción: el orden de esta lista es el orden de reproducción. */}
          <Stack spacing={1}>
            <Stack direction="row" alignItems="baseline" justifyContent="space-between" spacing={1}>
              <Typography variant="overline" sx={sectionLabelSx}>
                {t('albums.queue', { ns: 'play', defaultValue: 'Tu lista' })}
              </Typography>
              {selectedTracks.length > 0 && (
                <Typography variant="caption" sx={{ color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
                  {t('albums.queueSummary', {
                    ns: 'play',
                    count: selectedTracks.length,
                    time: formatDuration(totalSeconds),
                    defaultValue: '{{count}} pistas · {{time}}'
                  })}
                </Typography>
              )}
            </Stack>

            {selectedTracks.length === 0 ? (
              <Stack spacing={0.5} sx={{ ...listShellSx, px: 2, py: 2.5, textAlign: 'center' }}>
                <Typography variant="body2" fontWeight={700}>
                  {t('albums.queueEmptyTitle', { ns: 'play', defaultValue: 'Tu lista está vacía' })}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {t('albums.queueEmptyBody', { ns: 'play', defaultValue: 'Marca abajo las pistas que quieres escuchar y ordénalas a tu gusto.' })}
                </Typography>
              </Stack>
            ) : (
              <Stack spacing={0.75}>
                {selectedTracks.map((track, index) => (
                  <Stack
                    key={track.id}
                    direction="row"
                    spacing={0.5}
                    alignItems="center"
                    sx={{
                      px: 1.25,
                      py: 0.75,
                      borderRadius: '12px',
                      border: '1px solid',
                      borderColor: 'hairline.main',
                      backgroundColor: 'surface.raised'
                    }}>
                    <Typography
                      variant="caption"
                      sx={{
                        width: 20,
                        flex: 'none',
                        fontWeight: 800,
                        color: 'gold.main',
                        fontVariantNumeric: 'tabular-nums'
                      }}>
                      {index + 1}
                    </Typography>
                    <Stack spacing={0} sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={700} noWrap>
                        {track.title}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.disabled', fontVariantNumeric: 'tabular-nums' }}>
                        {formatDuration(track.duration_seconds)}
                      </Typography>
                    </Stack>
                    <IconButton
                      size="small"
                      onClick={() => move(index, -1)}
                      disabled={index === 0}
                      aria-label={t('albums.moveUp', { ns: 'play', defaultValue: 'Subir' })}>
                      <ArrowUpward sx={{ fontSize: 15 }} />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => move(index, 1)}
                      disabled={index === selectedTracks.length - 1}
                      aria-label={t('albums.moveDown', { ns: 'play', defaultValue: 'Bajar' })}>
                      <ArrowDownward sx={{ fontSize: 15 }} />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => toggleTrack(String(track.id))}
                      aria-label={t('albums.removeTrack', { ns: 'play', defaultValue: 'Quitar de la lista' })}
                      sx={{ color: 'sessionStatus.absent.fg' }}>
                      <DeleteOutline sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Stack>
                ))}
              </Stack>
            )}

            {emptyQueue && (
              <Typography variant="caption" sx={{ color: 'sessionStatus.absent.fg' }}>
                {t('albums.queueRequired', { ns: 'play', defaultValue: 'Añade al menos una pista.' })}
              </Typography>
            )}
          </Stack>

          {/* Librería con buscador y casillas. */}
          <Stack spacing={1}>
            <Typography variant="overline" sx={sectionLabelSx}>
              {t('albums.library', { ns: 'play', defaultValue: 'Pistas disponibles' })}
            </Typography>

            <TextField
              size="small"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('albums.search', { ns: 'play', defaultValue: 'Buscar por nombre o categoría' })}
              aria-label={t('albums.search', { ns: 'play', defaultValue: 'Buscar por nombre o categoría' })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ fontSize: 17, color: 'text.disabled' }} />
                  </InputAdornment>
                )
              }}
              fullWidth
            />

            <Box sx={{ ...listShellSx, maxHeight: 260, overflowY: 'auto' }}>
              {loading && !data && (
                <Stack spacing={0.75} sx={{ p: 1 }}>
                  {[0, 1, 2, 3].map((i) => (
                    <Skeleton key={i} variant="rounded" height={40} />
                  ))}
                </Stack>
              )}

              {!loading && library.length === 0 && (
                <Stack spacing={0.5} sx={{ px: 2, py: 2.5, textAlign: 'center' }}>
                  <Typography variant="body2" fontWeight={700}>
                    {t('albums.libraryEmptyTitle', { ns: 'play', defaultValue: 'Todavía no hay pistas' })}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {t('albums.libraryEmptyBody', { ns: 'play', defaultValue: 'Cuando subamos música a la librería podrás armar tus playlists.' })}
                  </Typography>
                </Stack>
              )}

              {library.length > 0 && filtered.length === 0 && (
                <Typography variant="caption" sx={{ display: 'block', px: 2, py: 2.5, textAlign: 'center', color: 'text.secondary' }}>
                  {t('albums.noResults', { ns: 'play', defaultValue: 'Ninguna pista coincide con tu búsqueda.' })}
                </Typography>
              )}

              {filtered.map((track) => {
                const id = String(track.id);
                const checked = selectedIds.includes(id);
                return (
                  <Stack
                    key={track.id}
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    component="label"
                    sx={{
                      px: 1,
                      py: 0.5,
                      cursor: 'pointer',
                      transition: `background-color 200ms ${EASE}`,
                      '&:hover': { backgroundColor: 'surface.raised' }
                    }}>
                    <Checkbox
                      size="small"
                      checked={checked}
                      onChange={() => toggleTrack(id)}
                      sx={{ color: 'text.disabled', '&.Mui-checked': { color: 'gold.main' } }}
                      inputProps={{ 'aria-label': track.title }}
                    />
                    <Stack spacing={0} sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={checked ? 700 : 500} noWrap>
                        {track.title}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }} noWrap>
                        {t(`musicAdmin.categories.${track.category}`, { ns: 'play', defaultValue: track.category })}
                      </Typography>
                    </Stack>
                    <Typography
                      variant="caption"
                      sx={{ flex: 'none', color: 'text.disabled', fontVariantNumeric: 'tabular-nums' }}>
                      {formatDuration(track.duration_seconds)}
                    </Typography>
                  </Stack>
                );
              })}
            </Box>
          </Stack>

          {failed && (
            <Typography variant="caption" sx={{ color: 'sessionStatus.absent.fg' }}>
              {t('albums.saveError', { ns: 'play', defaultValue: 'No pudimos guardar la playlist. Intenta de nuevo.' })}
            </Typography>
          )}

          <Button fullWidth onClick={handleSave} disabled={saving} sx={{ ...premiumButtonSx, py: 1.2 }}>
            {saving
              ? t('albums.saving', { ns: 'play', defaultValue: 'Guardando…' })
              : t('albums.save', { ns: 'play', defaultValue: 'Guardar playlist' })}
          </Button>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}

PlayAlbumBuilder.propTypes = {
  open: PropTypes.bool.isRequired,
  /** Álbum a editar (con sus `tracks` en orden); null = crear uno nuevo. */
  album: PropTypes.object,
  /** true = playlist del propio usuario; false = playlist curada por OTRIX (solo staff). */
  personal: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func
};

export default PlayAlbumBuilder;
